import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, CloudDownload, Upload, Film, FileText, Mic, Play, Plus,
  Loader2, Users, Trash2, MessageSquare, HardDrive, Check, Sparkles,
  Layers, X, ChevronRight, Folder, RefreshCw, Music,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { HowItWorks } from "@/components/HowItWorks";

const KANBAN = [
  { key: "todo", label: "A fazer", color: "#64748B" },
  { key: "editing", label: "Em edição", color: "#00E5FF" },
  { key: "review", label: "Revisão", color: "#F59E0B" },
  { key: "approved", label: "Aprovado", color: "#10B981" },
];

const statusBadge = {
  importado: { t: "Importado", c: "#64748B" },
  transcrito: { t: "Transcrito", c: "#00E5FF" },
  roteirizado: { t: "Roteirizado", c: "#FF2E93" },
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isProducer = user?.role === "producer";
  const [project, setProject] = useState(null);
  const [videos, setVideos] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [importing, setImporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [drive, setDrive] = useState({ configured: false, connected: false });
  const [driveOpen, setDriveOpen] = useState(false);
  const [driveHelpOpen, setDriveHelpOpen] = useState(false);
  const [driveItems, setDriveItems] = useState(null);
  const [drivePath, setDrivePath] = useState([]);
  const [selected, setSelected] = useState([]);
  const [driveBusy, setDriveBusy] = useState(false);
  const [importMode, setImportMode] = useState("proxy");
  const [importJob, setImportJob] = useState(null);
  const [storageInfo, setStorageInfo] = useState(null);
  const [cuts, setCuts] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [trackKind, setTrackKind] = useState("stinger");
  const [trackUploading, setTrackUploading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [picked, setPicked] = useState([]);
  const [creatingCut, setCreatingCut] = useState(false);

  const checkDrive = () => api.get("/drive/status").then((r) => setDrive(r.data)).catch(() => {});

  const load = async () => {
    const [p, v, e, pa, c] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/projects/${id}/videos`),
      api.get(`/projects/${id}/episodes`),
      api.get(`/projects/${id}/participants`),
      api.get(`/projects/${id}/cuts`),
    ]);
    setProject(p.data); setVideos(v.data); setEpisodes(e.data); setParticipants(pa.data); setCuts(c.data);
    api.get(`/projects/${id}/storage`).then((r) => setStorageInfo(r.data)).catch(() => {});
    api.get(`/projects/${id}/tracks`).then((r) => setTracks(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    api.get(`/projects/${id}/import-jobs`).then((r) => {
      if (r.data.length > 0) setImportJob(r.data[0]);
    }).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!importJob || importJob.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/drive/imports/${importJob.id}`);
        setImportJob(data);
        if (data.status === "done") {
          toast.success(`${data.imported} vídeo(s) importado(s) do Google Drive`);
          load();
        } else if (data.status === "interrupted") {
          toast.error("Importação interrompida — o servidor reiniciou durante o processo.");
        }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [importJob?.id, importJob?.status]);

  useEffect(() => {
    checkDrive();
    if (new URLSearchParams(window.location.search).get("drive_connected")) {
      toast.success("Google Drive conectado!");
      window.history.replaceState({}, "", `/projects/${id}`);
      checkDrive();
    }
  }, [id]);

  const retryImport = async () => {
    try {
      const { data } = await api.post(`/drive/imports/${importJob.id}/retry`);
      setImportJob(data);
      toast.success("Reimportação iniciada");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const dismissImport = async () => {
    try { await api.post(`/drive/imports/${importJob.id}/dismiss`); } catch { /* best effort */ }
    setImportJob(null);
  };

  const connectDrive = async () => {
    try {
      const { data } = await api.get("/drive/connect");
      window.location.href = data.authorization_url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const DRIVE_ROOTS = [
    { id: "__mydrive", name: "Meu Drive", is_folder: true, scope: "mydrive" },
    { id: "__shared", name: "Compartilhados comigo", is_folder: true, scope: "shared" },
    { id: "__shared_drives", name: "Drives compartilhados", is_folder: true, scope: "shared_drives" },
  ];

  const browseTo = async (path) => {
    setDrivePath(path);
    if (path.length === 0) {
      setDriveItems(DRIVE_ROOTS);
      return;
    }
    setDriveItems(null);
    const last = path[path.length - 1];
    const params = last.scope ? { scope: last.scope } : { folder: last.id };
    try {
      const { data } = await api.get("/drive/browse", { params });
      setDriveItems(data.items);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setDriveItems([]);
    }
  };

  const openDrivePicker = () => {
    setDriveOpen(true); setSelected([]);
    browseTo([]);
  };

  const importReal = async () => {
    if (selected.length === 0) return;
    setDriveBusy(true);
    try {
      const { data } = await api.post("/drive/import", {
        project_id: id, file_ids: selected.map((s) => s.id), mode: importMode,
      });
      setImportJob(data);
      setDriveOpen(false);
      setSelected([]);
      toast.info("Importação iniciada em segundo plano — acompanhe o progresso na biblioteca.");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setDriveBusy(false); }
  };

  const driveImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post(`/projects/${id}/drive/import`);
      toast.success(`${data.imported} vídeos importados do Drive (demo)`);
      await load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setImporting(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("project_id", id);
    fd.append("scene", "Upload manual");
    try {
      await api.post("/videos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Vídeo enviado");
      await load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setUploading(false); e.target.value = ""; }
  };

  const moveEpisode = async (eid, status) => {
    try {
      await api.patch(`/episodes/${eid}/status`, { status });
      setEpisodes((prev) => prev.map((ep) => (ep.id === eid ? { ...ep, status } : ep)));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const addEpisode = async () => {
    const num = episodes.length + 1;
    await api.post(`/projects/${id}/episodes`, { title: `Episódio ${num}`, number: num });
    load();
  };

  const addParticipant = async () => {
    const name = prompt("Nome do participante:");
    if (!name) return;
    const colors = ["#FF2E93", "#00E5FF", "#F59E0B", "#10B981", "#8B5CF6"];
    await api.post(`/projects/${id}/participants`, { name, color: colors[participants.length % colors.length] });
    load();
  };

  const deleteVideo = async (v) => {
    if (!window.confirm(`Excluir "${v.title}"? A transcrição e o roteiro deste vídeo também serão removidos.`)) return;
    try {
      await api.delete(`/videos/${v.id}`);
      setPicked((prev) => prev.filter((x) => x !== v.id));
      toast.success("Vídeo excluído");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const togglePick = (v) => {
    if (!v.storage_path) {
      toast.error(v.purged
        ? "O proxy deste vídeo foi apagado na limpeza — reimporte do Drive para usá-lo no episódio IA."
        : "Vídeos demo não podem entrar no episódio IA. Use vídeos enviados ou do Drive real.");
      return;
    }
    setPicked((prev) => (prev.includes(v.id) ? prev.filter((x) => x !== v.id) : [...prev, v.id]));
  };

  const createCut = async () => {
    const title = prompt("Nome do episódio:", `Episódio ${cuts.length + 1}`);
    if (title === null) return;
    setCreatingCut(true);
    toast.info("A IA está transcrevendo os vídeos e montando o episódio... isso pode levar alguns minutos.");
    try {
      const { data } = await api.post(`/projects/${id}/cuts`, { video_ids: picked, title: title || "" });
      toast.success("Episódio montado pela IA!");
      setPicked([]);
      navigate(`/cuts/${data.id}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setCreatingCut(false); }
  };

  const deleteCut = async (c) => {
    if (!window.confirm(`Excluir o episódio "${c.title}"?`)) return;
    try {
      await api.delete(`/cuts/${c.id}`);
      toast.success("Episódio excluído");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const trackRef = useRef(null);
  const uploadTrack = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setTrackUploading(true);
    const fd = new FormData();
    fd.append("kind", trackKind);
    fd.append("file", file);
    try {
      const { data } = await api.post(`/projects/${id}/tracks`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTracks((prev) => [...prev, data]);
      toast.success(`"${file.name}" adicionada — será usada nas cenas das próximas prévias`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setTrackUploading(false); e.target.value = ""; }
  };

  const deleteTrack = async (t) => {
    if (!window.confirm(`Remover "${t.title}" das trilhas do projeto?`)) return;
    try {
      await api.delete(`/tracks/${t.id}`);
      setTracks((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Trilha removida");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const purgeProxies = async () => {
    if (!window.confirm("Liberar espaço apagando os arquivos de vídeo (proxies) deste projeto?\n\nTranscrições, roteiros e a exportação XML/EDL continuam funcionando — a edição final usa os brutos originais no Premiere/DaVinci. A prévia em vídeo ficará indisponível.")) return;
    setPurging(true);
    try {
      const { data } = await api.post(`/projects/${id}/proxies/purge`);
      const fb = (b) => (b >= 1024 ** 3 ? (b / 1024 ** 3).toFixed(2) + " GB" : (b / 1024 ** 2).toFixed(1) + " MB");
      toast.success(`${data.purged} arquivo(s) apagados — ${fb(data.freed_bytes)} liberados`);
      if (data.failed > 0) toast.error(`${data.failed} arquivo(s) não puderam ser apagados`);
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setPurging(false); }
  };

  if (!project) return <div className="p-10"><Loader2 className="w-6 h-6 animate-spin text-[#FF2E93]" /></div>;

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <button onClick={() => navigate("/projects")} className="text-[#A1A1AA] hover:text-[#F4F4F5] flex items-center gap-2 text-sm mb-4 transition-colors duration-200" data-testid="back-button">
        <ArrowLeft className="w-4 h-4" /> Projetos
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 fade-up">
        <div>
          <h1 className="font-head font-700 text-4xl">{project.name}</h1>
          <p className="text-[#A1A1AA] mt-2 max-w-2xl">{project.description}</p>
          {project.editorial_line && (
            <p className="text-sm text-[#00E5FF] mt-2">Linha editorial: <span className="text-[#A1A1AA]">{project.editorial_line}</span></p>
          )}
          {storageInfo && storageInfo.total_bytes > 0 && (() => {
            const fb = (b) => (b >= 1024 ** 3 ? (b / 1024 ** 3).toFixed(2) + " GB" : (b / 1024 ** 2).toFixed(1) + " MB");
            return (
              <p className="text-xs text-[#555] mt-2 flex items-center gap-2 flex-wrap" data-testid="project-storage-usage">
                Espaço usado neste projeto: <span className="text-[#A1A1AA]">{fb(storageInfo.total_bytes)}</span>
                {" "}(vídeos {fb(storageInfo.videos_bytes)} · entregas {fb(storageInfo.exports_bytes)})
                {isProducer && storageInfo.videos_bytes > 0 && (
                  <button onClick={purgeProxies} disabled={purging} data-testid="purge-proxies-button"
                    title="Apaga os arquivos de vídeo (proxies) para liberar espaço. Transcrições, roteiros e XML/EDL continuam funcionando."
                    className="text-[11px] flex items-center gap-1 text-[#F59E0B] hover:text-[#fbbf24] underline underline-offset-2 disabled:opacity-50 transition-colors duration-200">
                    {purging ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    Liberar espaço (apagar proxies)
                  </button>
                )}
              </p>
            );
          })()}
        </div>
        <div className="flex flex-wrap gap-3">
          <HowItWorks />
          {isProducer && (
            <>
              {drive.configured ? (
                drive.connected ? (
                  <button onClick={openDrivePicker} data-testid="drive-picker-button"
                    className="bg-[#1C1C24] border border-[#282834] hover:border-[#10B981] text-[#F4F4F5] rounded-full px-4 py-2.5 flex items-center gap-2 text-sm transition-colors duration-200">
                    <HardDrive className="w-4 h-4 text-[#10B981]" /> Importar do Drive
                  </button>
                ) : (
                  <button onClick={connectDrive} data-testid="drive-connect-button"
                    className="bg-[#1C1C24] border border-[#282834] hover:border-[#10B981] text-[#F4F4F5] rounded-full px-4 py-2.5 flex items-center gap-2 text-sm transition-colors duration-200">
                    <HardDrive className="w-4 h-4 text-[#10B981]" /> Conectar Google Drive
                  </button>
                )
              ) : (
                <button onClick={() => setDriveHelpOpen(true)} data-testid="drive-help-button"
                  className="bg-[#1C1C24] border border-dashed border-[#282834] hover:border-[#F59E0B] text-[#A1A1AA] rounded-full px-4 py-2.5 flex items-center gap-2 text-sm transition-colors duration-200">
                  <HardDrive className="w-4 h-4 text-[#F59E0B]" /> Google Drive (configurar)
                </button>
              )}
              {videos.length === 0 && (
                <button onClick={driveImport} disabled={importing} data-testid="drive-import-button"
                  className="bg-[#1C1C24] border border-[#282834] hover:border-[#00E5FF] text-[#F4F4F5] rounded-full px-4 py-2.5 flex items-center gap-2 text-sm transition-colors duration-200 disabled:opacity-60">
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudDownload className="w-4 h-4 text-[#00E5FF]" />}
                  Importar demo
                </button>
              )}
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-video-button"
                className="bg-[#FF2E93] hover:bg-[#ff54a6] text-white rounded-full px-4 py-2.5 flex items-center gap-2 text-sm transition-colors duration-200 disabled:opacity-60">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Enviar vídeo/áudio
              </button>
              <input ref={fileRef} type="file" accept="video/*,audio/*" onChange={upload} className="hidden" data-testid="file-input" />
            </>
          )}
        </div>
      </div>

      <Tabs defaultValue="library" className="mt-8">
        <TabsList className="bg-[#121218] border border-[#282834]">
          <TabsTrigger value="library" data-testid="tab-library" className="data-[state=active]:bg-[#FF2E93] data-[state=active]:text-white">Biblioteca</TabsTrigger>
          <TabsTrigger value="cuts" data-testid="tab-cuts" className="data-[state=active]:bg-[#FF2E93] data-[state=active]:text-white">Episódios IA {cuts.length > 0 ? `(${cuts.length})` : ""}</TabsTrigger>
          <TabsTrigger value="production" data-testid="tab-production" className="data-[state=active]:bg-[#FF2E93] data-[state=active]:text-white">Produção (Kanban)</TabsTrigger>
          <TabsTrigger value="tracks" data-testid="tab-tracks" className="data-[state=active]:bg-[#FF2E93] data-[state=active]:text-white">Trilhas {tracks.length > 0 ? `(${tracks.length})` : ""}</TabsTrigger>
          <TabsTrigger value="cast" data-testid="tab-cast" className="data-[state=active]:bg-[#FF2E93] data-[state=active]:text-white">Participantes</TabsTrigger>
        </TabsList>

        {/* LIBRARY */}
        <TabsContent value="library" className="mt-6">
          {importJob && importJob.status === "running" && (() => {
            const done = importJob.files.filter((f) => ["concluído", "erro"].includes(f.status)).length;
            const fmtB = (b) => (b >= 1024 ** 3 ? (b / 1024 ** 3).toFixed(2) + " GB" : (b / 1024 ** 2).toFixed(1) + " MB");
            const fmtT = (s) => (s > 3600 ? `${Math.floor(s / 3600)}h${Math.floor((s % 3600) / 60)}m` : s > 60 ? `${Math.floor(s / 60)}m${Math.floor(s % 60)}s` : `${Math.max(1, Math.floor(s))}s`);
            return (
              <div data-testid="import-progress-banner" className="mb-4 bg-[#121218] border border-[#10B981]/40 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Loader2 className="w-5 h-5 animate-spin text-[#10B981] shrink-0" />
                  <p className="text-sm font-semibold">
                    Importando do Google Drive ({done}/{importJob.files.length})
                    {importJob.mode === "proxy" && <span className="text-[10px] text-[#10B981] ml-2">modo leve (proxy)</span>}
                  </p>
                  <span className="text-[11px] text-[#555] ml-auto hidden sm:block">você pode continuar usando o app</span>
                </div>
                <div className="space-y-2">
                  {importJob.files.map((f, idx) => {
                    const active = !["concluído", "erro", "na fila", "interrompido"].includes(f.status);
                    const hasBytes = active && f.phase_total > 0 && f.downloaded >= 0;
                    const pct = hasBytes ? Math.min(100, (f.downloaded / f.phase_total) * 100) : 0;
                    let eta = null;
                    if (hasBytes && f.phase_started && f.downloaded > 0) {
                      const elapsed = (Date.now() - new Date(f.phase_started).getTime()) / 1000;
                      const speed = f.downloaded / Math.max(elapsed, 1);
                      if (speed > 0) eta = (f.phase_total - f.downloaded) / speed;
                    }
                    return (
                      <div key={idx} data-testid={`import-file-row-${idx}`} className="bg-[#08080C] rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 text-xs">
                          {f.status === "concluído" ? <Check className="w-3.5 h-3.5 text-[#10B981] shrink-0" />
                            : f.status === "erro" ? <X className="w-3.5 h-3.5 text-[#EF4444] shrink-0" />
                            : active ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#10B981] shrink-0" />
                            : <span className="w-3.5 h-3.5 rounded-full border border-[#3a3a48] shrink-0" />}
                          <span className="font-mono truncate flex-1">{f.name || "..."}</span>
                          <span className={`shrink-0 ${f.status === "erro" ? "text-[#EF4444]" : "text-[#A1A1AA]"}`}>
                            {f.status}{f.size ? ` · ${fmtB(f.size)}` : ""}
                          </span>
                        </div>
                        {hasBytes && (
                          <div className="flex items-center gap-2 mt-1.5">
                            <div className="flex-1 h-1.5 rounded-full bg-[#282834] overflow-hidden">
                              <div className="h-full bg-[#10B981] transition-all duration-500" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-[10px] text-[#A1A1AA] shrink-0 font-mono">
                              {pct.toFixed(0)}%{eta != null && eta > 1 ? ` · ~${fmtT(eta)} restantes` : ""}
                            </span>
                          </div>
                        )}
                        {f.status === "erro" && f.error && <p className="text-[10px] text-[#EF4444] mt-1">{f.error}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {importJob && importJob.status === "interrupted" && (
            <div data-testid="import-interrupted-banner" className="mb-4 bg-[#F59E0B]/10 border border-[#F59E0B]/40 rounded-xl p-4">
              <p className="text-sm font-semibold text-[#F59E0B]">Importação interrompida</p>
              <p className="text-xs text-[#A1A1AA] mt-1">
                O servidor reiniciou durante o processo. O sistema tentou retomar automaticamente, mas não conseguiu concluir.
                Nada foi perdido — clique abaixo para retomar os arquivos que faltaram.
              </p>
              {importJob.files.filter((f) => f.status !== "concluído").map((f) => (
                <p key={f.fid} className="text-xs text-[#A1A1AA] mt-1 font-mono">• {f.name || f.fid}</p>
              ))}
              <div className="flex items-center gap-3 mt-3">
                <button onClick={retryImport} data-testid="retry-import-button"
                  className="bg-[#F59E0B] hover:bg-[#fbbf24] text-black rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-200 flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5" /> Tentar novamente
                </button>
                <button onClick={dismissImport} data-testid="dismiss-import-button" className="text-xs text-[#A1A1AA] underline">descartar</button>
              </div>
            </div>
          )}
          {importJob && importJob.status === "done" && importJob.files.some((f) => f.status === "erro") && (
            <div data-testid="import-errors-banner" className="mb-4 bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-xl p-4">
              <p className="text-sm font-semibold text-[#EF4444]">Alguns arquivos falharam na importação:</p>
              {importJob.files.filter((f) => f.status === "erro").map((f) => (
                <p key={f.fid} className="text-xs text-[#A1A1AA] mt-1">• {f.name || f.fid}: {f.error}</p>
              ))}
              <div className="flex items-center gap-3 mt-2">
                <button onClick={retryImport} data-testid="retry-failed-import-button"
                  className="bg-[#EF4444] hover:bg-[#f87171] text-white rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-200 flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5" /> Tentar novamente
                </button>
                <button onClick={dismissImport} className="text-xs text-[#A1A1AA] underline">fechar</button>
              </div>
            </div>
          )}
          {videos.length > 0 && (
            <p className="text-sm text-[#A1A1AA] mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#00E5FF]" />
              Selecione vários vídeos (caixa no canto do card) e deixe a IA montar o episódio inteiro cruzando o material.
            </p>
          )}
          {videos.length === 0 ? (
            <div className="bg-[#121218] border border-dashed border-[#282834] rounded-xl p-16 text-center">
              <Film className="w-10 h-10 text-[#282834] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">Nenhum vídeo ainda. Envie os brutos do seu computador ou importe do Google Drive para começar.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {videos.map((v) => {
                const sb = statusBadge[v.status] || statusBadge.importado;
                const isPicked = picked.includes(v.id);
                return (
                  <div key={v.id} data-testid={`video-card-${v.id}`}
                    className={`bg-[#121218] border rounded-xl overflow-hidden transition-colors duration-200 group ${isPicked ? "border-[#00E5FF]" : "border-[#282834] hover:border-[#3a3a48]"}`}>
                    <div className="h-36 relative overflow-hidden bg-black">
                      <img src={v.thumbnail || undefined} alt="" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-200" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <button onClick={() => navigate(`/editor/${v.id}`)} data-testid={`open-editor-${v.id}`}
                          className="w-12 h-12 rounded-full bg-[#FF2E93]/90 hover:bg-[#FF2E93] flex items-center justify-center transition-colors duration-200">
                          <Play className="w-5 h-5 text-white ml-0.5" />
                        </button>
                      </div>
                      <button onClick={() => togglePick(v)} data-testid={`pick-video-${v.id}`}
                        title={v.purged ? "Proxy removido — não pode entrar no episódio IA" : v.storage_path ? "Selecionar para o episódio IA" : "Vídeo demo — não pode entrar no episódio IA"}
                        className={`absolute top-2 left-2 w-6 h-6 rounded flex items-center justify-center border transition-colors duration-200 ${isPicked ? "bg-[#00E5FF] border-[#00E5FF]" : v.storage_path ? "bg-black/60 border-[#3a3a48] hover:border-[#00E5FF]" : "bg-black/40 border-[#282834] opacity-50"}`}>
                        {isPicked && <Check className="w-4 h-4 text-black" />}
                      </button>
                      <span className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full bg-black/60 text-[#A1A1AA]">{v.source === "drive" ? "Demo" : v.source === "drive_real" ? (v.quality === "proxy" ? "Drive · Proxy" : "Drive") : "Upload"}</span>
                      {v.purged && (
                        <span data-testid={`purged-badge-${v.id}`} className="absolute bottom-2 right-2 text-[10px] px-2 py-0.5 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/40">proxy removido</span>
                      )}
                      <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: sb.c + "22", color: sb.c }}>{sb.t}</span>
                    </div>
                    <div className="p-4">
                      <p className="text-sm font-mono truncate text-[#F4F4F5]" title={v.title}>{v.title}</p>
                      <p className="text-xs text-[#A1A1AA] mt-1">{v.scene}</p>
                      <div className="flex gap-2 mt-3">
                        <span className={`text-[10px] px-2 py-1 rounded flex items-center gap-1 ${v.has_transcription ? "text-[#00E5FF]" : "text-[#555]"}`}><Mic className="w-3 h-3" /> Transcrição</span>
                        <span className={`text-[10px] px-2 py-1 rounded flex items-center gap-1 ${v.has_script ? "text-[#FF2E93]" : "text-[#555]"}`}><FileText className="w-3 h-3" /> Roteiro</span>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button onClick={() => navigate(`/editor/${v.id}`)} data-testid={`edit-map-${v.id}`}
                          className="flex-1 bg-[#1C1C24] hover:bg-[#282834] text-sm rounded-lg py-2 transition-colors duration-200">
                          Abrir mapa de edição
                        </button>
                        {isProducer && (
                          <button onClick={() => deleteVideo(v)} data-testid={`delete-video-${v.id}`}
                            title="Excluir vídeo"
                            className="shrink-0 w-9 bg-[#1C1C24] hover:bg-[#EF4444]/15 text-[#555] hover:text-[#EF4444] rounded-lg flex items-center justify-center transition-colors duration-200">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* EPISODE CUTS (AI) */}
        <TabsContent value="cuts" className="mt-6">
          {cuts.length === 0 ? (
            <div className="bg-[#121218] border border-dashed border-[#282834] rounded-xl p-16 text-center">
              <Layers className="w-10 h-10 text-[#282834] mx-auto mb-4" />
              <p className="text-[#A1A1AA] max-w-md mx-auto">Nenhum episódio montado ainda. Na Biblioteca, selecione vários vídeos e clique em <span className="text-[#00E5FF]">Montar episódio com IA</span>.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cuts.map((c) => (
                <div key={c.id} data-testid={`cut-card-${c.id}`}
                  className="bg-[#121218] border border-[#282834] rounded-xl p-4 flex items-center gap-4 hover:border-[#3a3a48] transition-colors duration-200">
                  <span className="w-10 h-10 rounded-lg bg-[#FF2E93]/15 flex items-center justify-center shrink-0">
                    <Layers className="w-5 h-5 text-[#FF2E93]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{c.title}</p>
                    <p className="text-xs text-[#A1A1AA] mt-0.5">
                      {c.video_ids?.length} vídeos de origem · {c.moments?.length} cenas
                      {c.status === "approved" && <span className="text-[#10B981] ml-2">✓ Aprovado</span>}
                    </p>
                  </div>
                  {isProducer && (
                    <button onClick={() => deleteCut(c)} data-testid={`delete-cut-${c.id}`}
                      className="shrink-0 w-9 h-9 bg-[#1C1C24] hover:bg-[#EF4444]/15 text-[#555] hover:text-[#EF4444] rounded-lg flex items-center justify-center transition-colors duration-200">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => navigate(`/cuts/${c.id}`)} data-testid={`open-cut-${c.id}`}
                    className="shrink-0 bg-[#1C1C24] border border-[#282834] hover:border-[#FF2E93] text-sm rounded-full px-4 py-2 flex items-center gap-1.5 transition-colors duration-200">
                    Abrir episódio <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* KANBAN */}
        <TabsContent value="production" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[#A1A1AA]">Arraste o status de cada episódio até a aprovação final.</p>
            {isProducer && (
              <button onClick={addEpisode} data-testid="add-episode-button" className="text-sm flex items-center gap-2 bg-[#1C1C24] border border-[#282834] hover:border-[#00E5FF] rounded-full px-4 py-2 transition-colors duration-200">
                <Plus className="w-4 h-4" /> Novo episódio
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {KANBAN.map((col) => {
              const items = episodes.filter((e) => e.status === col.key);
              return (
                <div key={col.key} data-testid={`kanban-col-${col.key}`} className="bg-[#0d0d12] border border-[#282834] rounded-xl p-3 min-h-[200px]">
                  <div className="flex items-center gap-2 px-2 py-2 mb-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: col.color }} />
                    <span className="text-sm font-head font-600">{col.label}</span>
                    <span className="text-xs text-[#A1A1AA] ml-auto">{items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {items.map((ep) => (
                      <EpisodeCard key={ep.id} ep={ep} col={col} onMove={moveEpisode} isProducer={isProducer} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* CAST */}
        {/* CUSTOM TRACKS */}
        <TabsContent value="tracks" className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-sm text-[#A1A1AA] max-w-2xl">
              <Music className="w-4 h-4 text-[#FF2E93] inline mr-1.5 -mt-0.5" />
              Suas músicas e stingers são usados nas cenas das prévias geradas pela IA: <span className="text-[#F4F4F5]">stingers</span> tocam nos momentos de treta/tensão e a <span className="text-[#F4F4F5]">trilha de fundo</span> roda em volume baixo no episódio todo (ative &quot;Trilha sonora&quot; ao gerar a prévia).
            </p>
            {isProducer && (
              <div className="flex items-center gap-2">
                <select value={trackKind} onChange={(e) => setTrackKind(e.target.value)} data-testid="track-kind-select"
                  className="bg-[#1C1C24] border border-[#282834] text-sm rounded-lg px-3 py-2 text-[#F4F4F5] focus:outline-none focus:border-[#FF2E93]">
                  <option value="stinger">Stinger (impacto)</option>
                  <option value="trilha">Trilha de fundo</option>
                </select>
                <button onClick={() => trackRef.current?.click()} disabled={trackUploading} data-testid="upload-track-button"
                  className="bg-[#FF2E93] hover:bg-[#ff54a6] text-white rounded-full px-4 py-2 flex items-center gap-2 text-sm transition-colors duration-200 disabled:opacity-60">
                  {trackUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Enviar áudio
                </button>
                <input ref={trackRef} type="file" accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg" onChange={uploadTrack} className="hidden" data-testid="track-file-input" />
              </div>
            )}
          </div>
          {tracks.length === 0 ? (
            <div className="bg-[#121218] border border-dashed border-[#282834] rounded-xl p-16 text-center">
              <Music className="w-10 h-10 text-[#282834] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">Nenhuma trilha ainda. {isProducer ? "Envie MP3/WAV com suas músicas e stingers — a IA usa nas cenas das prévias." : "O produtor ainda não enviou trilhas para este projeto."}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tracks.map((t) => (
                <div key={t.id} data-testid={`track-row-${t.id}`}
                  className="bg-[#121218] border border-[#282834] rounded-xl p-4 flex flex-wrap items-center gap-4 hover:border-[#3a3a48] transition-colors duration-200">
                  <span className="w-10 h-10 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center shrink-0">
                    <Music className="w-5 h-5 text-[#00E5FF]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-mono truncate" title={t.title}>{t.title}</p>
                    <p className="text-xs text-[#A1A1AA] mt-0.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider mr-2 ${t.kind === "trilha" ? "bg-[#8B5CF6]/15 text-[#8B5CF6]" : "bg-[#F59E0B]/15 text-[#F59E0B]"}`}>
                        {t.kind === "trilha" ? "Trilha de fundo" : "Stinger"}
                      </span>
                      {(t.size / 1024 ** 2).toFixed(1)} MB
                    </p>
                  </div>
                  <audio controls preload="none" src={`${API}/tracks/${t.id}/stream`} className="h-9 max-w-[280px]" data-testid={`track-player-${t.id}`} />
                  {isProducer && (
                    <button onClick={() => deleteTrack(t)} data-testid={`delete-track-${t.id}`}
                      className="shrink-0 w-9 h-9 bg-[#1C1C24] hover:bg-[#EF4444]/15 text-[#555] hover:text-[#EF4444] rounded-lg flex items-center justify-center transition-colors duration-200">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="cast" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[#A1A1AA]">Participantes do reality.</p>
            {isProducer && (
              <button onClick={addParticipant} data-testid="add-participant-button" className="text-sm flex items-center gap-2 bg-[#1C1C24] border border-[#282834] hover:border-[#00E5FF] rounded-full px-4 py-2 transition-colors duration-200">
                <Plus className="w-4 h-4" /> Adicionar
              </button>
            )}
          </div>
          {participants.length === 0 ? (
            <div className="bg-[#121218] border border-dashed border-[#282834] rounded-xl p-12 text-center">
              <Users className="w-8 h-8 text-[#282834] mx-auto mb-3" />
              <p className="text-[#A1A1AA]">Nenhum participante cadastrado.</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              {participants.map((pa) => (
                <div key={pa.id} className="flex items-center gap-3 bg-[#121218] border border-[#282834] rounded-full pl-1.5 pr-5 py-1.5">
                  <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-black" style={{ background: pa.color }}>{pa.name.charAt(0)}</span>
                  <span className="text-sm">{pa.name}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Floating action bar for multi-video AI episode */}
      {picked.length > 0 && (
        <div data-testid="cut-action-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#121218] border border-[#00E5FF]/40 rounded-full shadow-2xl px-5 py-3 flex items-center gap-4">
          <span className="text-sm text-[#F4F4F5]">
            <span className="text-[#00E5FF] font-semibold">{picked.length}</span> vídeo{picked.length > 1 ? "s" : ""} selecionado{picked.length > 1 ? "s" : ""}
          </span>
          <button onClick={createCut} disabled={creatingCut} data-testid="create-cut-button"
            className="bg-[#FF2E93] hover:bg-[#ff54a6] text-white rounded-full px-5 py-2 flex items-center gap-2 text-sm font-semibold transition-colors duration-200 disabled:opacity-60">
            {creatingCut ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {creatingCut ? "Montando episódio..." : "Montar episódio com IA"}
          </button>
          <button onClick={() => setPicked([])} disabled={creatingCut} data-testid="clear-picked-button"
            className="text-[#A1A1AA] hover:text-[#F4F4F5] transition-colors duration-200">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Drive not configured help */}
      <Dialog open={driveHelpOpen} onOpenChange={setDriveHelpOpen}>
        <DialogContent className="bg-[#121218] border-[#282834] text-[#F4F4F5] max-w-lg" data-testid="drive-help-dialog">
          <DialogHeader>
            <DialogTitle className="font-head text-xl flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-[#F59E0B]" /> Google Drive não configurado
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Este ambiente ainda não tem as credenciais do Google configuradas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#A1A1AA]">
            <p>Para o botão <span className="text-[#10B981]">Conectar Google Drive</span> aparecer aqui, é preciso:</p>
            <ol className="list-decimal ml-5 space-y-2">
              <li>Ter <span className="font-mono text-xs text-[#F4F4F5]">GOOGLE_CLIENT_ID</span> e <span className="font-mono text-xs text-[#F4F4F5]">GOOGLE_CLIENT_SECRET</span> nas variáveis de ambiente do servidor (em produção, configure no painel de deploy da Emergent e faça o redeploy).</li>
              <li>No Google Cloud Console, cadastrar a URL de redirecionamento autorizada:<br />
                <span className="font-mono text-[11px] text-[#00E5FF] break-all">{`${window.location.origin}/api/drive/callback`}</span>
              </li>
            </ol>
            <p className="text-xs text-[#555]">Enquanto isso, você pode enviar os vídeos direto do computador com o botão rosa.</p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={driveOpen} onOpenChange={setDriveOpen}>
        <DialogContent className="bg-[#121218] border-[#282834] text-[#F4F4F5] max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-head text-xl flex items-center gap-2"><HardDrive className="w-5 h-5 text-[#10B981]" /> Importar do Google Drive</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Navegue pelas pastas (inclusive compartilhadas) e selecione os vídeos brutos.</DialogDescription>
          </DialogHeader>

          {/* Breadcrumb */}
          <div className="flex items-center gap-1 flex-wrap text-xs" data-testid="drive-breadcrumb">
            <button onClick={() => browseTo([])} className="text-[#10B981] hover:underline shrink-0">Drive</button>
            {drivePath.map((p, i) => (
              <span key={p.id} className="flex items-center gap-1 min-w-0">
                <ChevronRight className="w-3 h-3 text-[#555] shrink-0" />
                <button onClick={() => browseTo(drivePath.slice(0, i + 1))}
                  className={`truncate max-w-[140px] ${i === drivePath.length - 1 ? "text-[#F4F4F5]" : "text-[#10B981] hover:underline"}`}>
                  {p.name}
                </button>
              </span>
            ))}
          </div>

          <div className="max-h-80 overflow-y-auto space-y-1.5" data-testid="drive-items">
            {driveItems === null && <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#10B981] mx-auto" /></div>}
            {driveItems && driveItems.length === 0 && <p className="text-sm text-[#A1A1AA] py-6 text-center">Nada aqui — nenhuma pasta ou vídeo encontrado.</p>}
            {driveItems && driveItems.map((f) => {
              if (f.is_folder) {
                return (
                  <button key={f.id} data-testid={`drive-folder-${f.id}`}
                    onClick={() => browseTo([...drivePath, f])}
                    className="w-full text-left flex items-center gap-3 rounded-lg px-3 py-2.5 border border-[#282834] hover:border-[#10B981] transition-colors duration-200">
                    <Folder className="w-4 h-4 text-[#10B981] shrink-0" />
                    <span className="text-sm truncate flex-1">{f.name}</span>
                    <ChevronRight className="w-4 h-4 text-[#555] shrink-0" />
                  </button>
                );
              }
              const on = selected.some((s) => s.id === f.id);
              return (
                <button key={f.id} data-testid={`drive-file-${f.id}`}
                  onClick={() => setSelected(on ? selected.filter((s) => s.id !== f.id) : [...selected, f])}
                  className={`w-full text-left flex items-center gap-3 rounded-lg px-3 py-2.5 border transition-colors duration-200 ${on ? "border-[#10B981] bg-[#10B981]/10" : "border-[#282834] hover:border-[#3a3a48]"}`}>
                  <span className={`w-5 h-5 rounded flex items-center justify-center border shrink-0 ${on ? "bg-[#10B981] border-[#10B981]" : "border-[#3a3a48]"}`}>
                    {on && <Check className="w-3.5 h-3.5 text-black" />}
                  </span>
                  <Film className="w-4 h-4 text-[#A1A1AA] shrink-0" />
                  <span className="text-sm truncate flex-1">{f.name}</span>
                  <span className="text-[10px] text-[#A1A1AA] shrink-0">{f.size ? (f.size / 1024 / 1024).toFixed(1) + " MB" : ""}</span>
                </button>
              );
            })}
          </div>

          {selected.length > 0 && (
            <p className="text-xs text-[#A1A1AA]">
              Selecionados: {selected.map((s) => s.name).join(", ").slice(0, 120)}{selected.map((s) => s.name).join(", ").length > 120 ? "..." : ""}
            </p>
          )}

          {/* Import mode */}
          <div className="grid grid-cols-2 gap-2" data-testid="import-mode-selector">
            <button onClick={() => setImportMode("proxy")} data-testid="mode-proxy"
              className={`text-left rounded-lg p-3 border transition-colors duration-200 ${importMode === "proxy" ? "border-[#10B981] bg-[#10B981]/10" : "border-[#282834] hover:border-[#3a3a48]"}`}>
              <p className="text-sm font-semibold">Leve (proxy 360p) <span className="text-[10px] text-[#10B981]">recomendado</span></p>
              <p className="text-[11px] text-[#A1A1AA] mt-0.5 leading-snug">Prévia que carrega na hora. IA e cortes funcionam igual; a finalização em alta usa o XML/EDL no Premiere/DaVinci.</p>
            </button>
            <button onClick={() => setImportMode("full")} data-testid="mode-full"
              className={`text-left rounded-lg p-3 border transition-colors duration-200 ${importMode === "full" ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-[#282834] hover:border-[#3a3a48]"}`}>
              <p className="text-sm font-semibold">Qualidade original</p>
              <p className="text-[11px] text-[#A1A1AA] mt-0.5 leading-snug">Guarda o arquivo inteiro no app (até 5GB por arquivo). Use para poucos arquivos pequenos.</p>
            </button>
          </div>

          <button onClick={importReal} disabled={selected.length === 0 || driveBusy} data-testid="drive-import-confirm"
            className="w-full bg-[#10B981] hover:bg-[#0ea371] text-white font-semibold rounded-full py-3 flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-50">
            {driveBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudDownload className="w-4 h-4" />}
            {driveBusy ? "Importando..." : `Importar ${selected.length > 0 ? `(${selected.length})` : ""}`}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EpisodeCard({ ep, col, onMove, isProducer }) {
  const [showComments, setShowComments] = useState(false);
  const next = { todo: "editing", editing: "review", review: "approved" }[ep.status];
  const prev = { editing: "todo", review: "editing", approved: "review" }[ep.status];
  return (
    <div data-testid={`kanban-card-${ep.id}`} className="bg-[#1C1C24] rounded-lg p-3 hover:-translate-y-0.5 transition-transform duration-200" style={{ borderLeft: `2px solid ${col.color}` }}>
      <p className="text-sm font-medium">{ep.title}</p>
      <div className="flex items-center gap-2 mt-3">
        {prev && (
          <button onClick={() => onMove(ep.id, prev)} data-testid={`move-prev-${ep.id}`} className="text-xs text-[#A1A1AA] hover:text-[#F4F4F5] transition-colors duration-200">← Voltar</button>
        )}
        {next && (
          <button onClick={() => onMove(ep.id, next)} data-testid={`move-next-${ep.id}`}
            disabled={next === "approved" && !isProducer}
            className="text-xs ml-auto text-[#00E5FF] hover:text-[#FF2E93] transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed">
            {next === "approved" ? "Aprovar →" : "Avançar →"}
          </button>
        )}
        <button onClick={() => setShowComments((s) => !s)} data-testid={`comments-toggle-${ep.id}`} className="text-[#A1A1AA] hover:text-[#F4F4F5] transition-colors duration-200">
          <MessageSquare className="w-3.5 h-3.5" />
        </button>
      </div>
      {showComments && <Comments targetId={ep.id} />}
    </div>
  );
}

function Comments({ targetId }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const load = () => api.get(`/comments`, { params: { target_id: targetId } }).then((r) => setComments(r.data));
  useEffect(() => { load(); }, [targetId]);
  const send = async () => {
    if (!text.trim()) return;
    await api.post("/comments", { target_id: targetId, target_type: "episode", text });
    setText(""); load();
  };
  return (
    <div className="mt-3 pt-3 border-t border-[#282834]">
      <div className="space-y-2 max-h-32 overflow-y-auto mb-2">
        {comments.map((c) => (
          <div key={c.id} className="text-xs">
            <span className="text-[#00E5FF]">{c.user_name}</span>
            <span className="text-[#555] ml-1">{c.user_role === "producer" ? "· produtor" : ""}</span>
            <p className="text-[#A1A1AA]">{c.text}</p>
          </div>
        ))}
        {comments.length === 0 && <p className="text-xs text-[#555]">Sem comentários.</p>}
      </div>
      <div className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          data-testid={`comment-input-${targetId}`} placeholder="Comentar..."
          className="flex-1 bg-[#08080C] border border-[#282834] rounded px-2 py-1 text-xs focus:outline-none focus:border-[#00E5FF]" />
        <button onClick={send} data-testid={`comment-send-${targetId}`} className="text-xs text-[#00E5FF] px-2">Enviar</button>
      </div>
    </div>
  );
}
